/**
 * RulesPage - Main page for managing processing rules.
 */

import { DeleteConfirmDialog } from "@/components/DeleteConfirmDialog";
import { RulesSidebar } from "@/components/rules/RulesSidebar";
import { RulesTable } from "@/components/rules/RulesTable";
import { TopNav } from "@/components/TopNav";
import { useDeleteRule, useInfiniteRules, useRules } from "@/hooks/useRules";
import { AGENT_TYPES, AGENT_TYPE_LABELS, AgentType, ProcessingRule } from "@/types/rules";
import { Button, CircularProgress, MenuItem, Pagination, TextField } from "@mui/material";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export function RulesPage() {
  const { enqueueSnackbar } = useSnackbar();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarMode, setSidebarMode] = useState<"create" | "edit">("create");
  const [selectedRule, setSelectedRule] = useState<ProcessingRule | undefined>();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<ProcessingRule | null>(null);

  // Filters
  const [agentTypeFilter, setAgentTypeFilter] = useState<string>("");
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [scopeFilter, setScopeFilter] = useState<string>("all");
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const observerTarget = useRef<HTMLDivElement>(null);

  // Debounce search input
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchText]);

  // Build filters for API
  const baseFilters = useMemo(
    () => ({
      agent_type: agentTypeFilter || undefined,
      is_active: activeFilter === "all" ? undefined : activeFilter === "active",
      search: debouncedSearchText || undefined,
    }),
    [agentTypeFilter, activeFilter, debouncedSearchText]
  );
  const useInfiniteScroll = agentTypeFilter === "";

  const {
    data: infiniteData,
    isLoading: isInfiniteLoading,
    error: infiniteError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteRules(useInfiniteScroll ? baseFilters : undefined);

  const paginatedFilters = useMemo(
    () => ({
      ...baseFilters,
      limit: pageSize,
      offset: (page - 1) * pageSize,
    }),
    [baseFilters, pageSize, page]
  );

  const {
    data: paginatedData,
    isLoading: isPaginatedLoading,
    error: paginatedError,
  } = useRules(useInfiniteScroll ? undefined : paginatedFilters);

  const deleteRuleMutation = useDeleteRule();

  // Combine data based on mode
  const data = useInfiniteScroll
    ? infiniteData?.pages[infiniteData.pages.length - 1]
    : paginatedData;
  const allRules = useMemo(() => {
    return useInfiniteScroll
      ? infiniteData?.pages.flatMap((page) => page.rules) || []
      : paginatedData?.rules || [];
  }, [useInfiniteScroll, infiniteData?.pages, paginatedData?.rules]);
  const isLoading = useInfiniteScroll ? isInfiniteLoading : isPaginatedLoading;
  const error = useInfiniteScroll ? infiniteError : paginatedError;

  // Reset to page 1 when filters change (pagination mode only)
  useEffect(() => {
    if (!useInfiniteScroll) {
      setPage(1);
    }
  }, [agentTypeFilter, activeFilter, scopeFilter, debouncedSearchText, useInfiniteScroll]);

  // Infinite scroll observer
  useEffect(() => {
    if (!useInfiniteScroll || !observerTarget.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(observerTarget.current);

    return () => observer.disconnect();
  }, [useInfiniteScroll, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Filter rules by scope on frontend (global vs specific)
  const filteredRules = useMemo(() => {
    return allRules.filter((rule) => {
      if (scopeFilter === "all") return true;
      const isGlobal =
        !rule.document_type && !rule.source_type && !rule.carrier_code && !rule.customer_ref_code;
      if (scopeFilter === "global") return isGlobal;
      if (scopeFilter === "specific") return !isGlobal;
      return true;
    });
  }, [allRules, scopeFilter]);

  // Calculate pagination based on filtered results (pagination mode only)
  const totalFilteredCount = filteredRules.length;
  const totalPages = Math.ceil(totalFilteredCount / pageSize);
  const totalCount = useInfiniteScroll
    ? infiniteData?.pages[0]?.total || 0
    : paginatedData?.total || 0;

  const handleCreateRule = useCallback(() => {
    setSelectedRule(undefined);
    setSidebarMode("create");
    setSidebarOpen(true);
  }, []);

  const handleEditRule = useCallback((rule: ProcessingRule) => {
    setSelectedRule(rule);
    setSidebarMode("edit");
    setSidebarOpen(true);
  }, []);

  const handleDeleteRule = useCallback((rule: ProcessingRule) => {
    setRuleToDelete(rule);
    setDeleteDialogOpen(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!ruleToDelete) return;

    try {
      await deleteRuleMutation.mutateAsync(ruleToDelete.id);
      enqueueSnackbar("Rule deleted successfully", { variant: "success" });
      setDeleteDialogOpen(false);
      setRuleToDelete(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      enqueueSnackbar(`Failed to delete rule: ${message}`, { variant: "error" });
    }
  }, [ruleToDelete, deleteRuleMutation, enqueueSnackbar]);

  const cancelDelete = useCallback(() => {
    setDeleteDialogOpen(false);
    setRuleToDelete(null);
  }, []);

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-light dark:bg-background-dark">
      <TopNav />
      <main className="flex-1 p-4 sm:p-6 md:p-8">
        <div className="mx-auto max-w-[1280px]">
          {/* Header Section */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-black tracking-tight text-slate-900 dark:text-white">
                Agent Processing Rules
              </h1>
              <p className="mt-1 text-slate-500 dark:text-slate-400">
                Manage and configure rule-based logic for processing agents.
              </p>
            </div>
            <Button
              variant="contained"
              onClick={handleCreateRule}
              startIcon={<span className="material-symbols-outlined text-xl">add_circle</span>}
              className="shadow-sm"
            >
              Create Rule
            </Button>
          </div>

          {/* Filter Toolbar */}
          <div className="mb-8 flex flex-wrap items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            {/* Search */}
            <div className="relative min-w-[300px] flex-1">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                search
              </span>
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search rules by content, ID, or agent..."
                className="w-full rounded-lg border-none bg-slate-50 py-2 pl-10 pr-4 text-sm focus:ring-2 focus:ring-primary/20 dark:bg-slate-800"
              />
            </div>

            <div className="flex items-center gap-3">
              {/* Active Status Filter */}
              <TextField
                select
                value={activeFilter}
                onChange={(e) => setActiveFilter(e.target.value)}
                size="small"
                className="w-40 bg-slate-50 dark:bg-slate-800"
              >
                <MenuItem value="all">Status: All</MenuItem>
                <MenuItem value="active">Status: Active</MenuItem>
                <MenuItem value="inactive">Status: Inactive</MenuItem>
              </TextField>

              {/* Scope Filter */}
              <TextField
                select
                value={scopeFilter}
                onChange={(e) => setScopeFilter(e.target.value)}
                size="small"
                className="w-40 bg-slate-50 dark:bg-slate-800"
              >
                <MenuItem value="all">Scope: All</MenuItem>
                <MenuItem value="global">Scope: Global</MenuItem>
                <MenuItem value="specific">Scope: Specific</MenuItem>
              </TextField>

              {/* Filter Icon Button */}
              <button className="rounded-lg p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                <span className="material-symbols-outlined">filter_list</span>
              </button>
            </div>
          </div>

          {/* Agent Type Tabs */}
          <div className="mb-6 flex items-center gap-1 overflow-x-auto border-b border-slate-200 pb-px dark:border-slate-800">
            <button
              onClick={() => setAgentTypeFilter("")}
              className={`whitespace-nowrap px-4 py-2 text-sm font-bold transition-colors ${
                agentTypeFilter === ""
                  ? "-mb-px border-b-2 border-primary text-primary"
                  : "font-medium text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
              }`}
            >
              All Rules
            </button>
            {AGENT_TYPES.map((type) => (
              <button
                key={type}
                onClick={() => setAgentTypeFilter(type)}
                className={`whitespace-nowrap px-4 py-2 text-sm transition-colors ${
                  agentTypeFilter === type
                    ? "-mb-px border-b-2 border-primary font-bold text-primary"
                    : "font-medium text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
                }`}
              >
                {AGENT_TYPE_LABELS[type as AgentType]}
              </button>
            ))}
          </div>

          {/* Error State */}
          {error && (
            <div className="mb-8 rounded-xl border border-red-200 bg-red-50 p-4 text-red-800 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined">error</span>
                <span>Failed to load rules: {error.message}</span>
              </div>
            </div>
          )}

          {/* Rules Table */}
          <RulesTable
            rules={filteredRules}
            isLoading={isLoading}
            onEdit={handleEditRule}
            onDelete={handleDeleteRule}
          />

          {/* Infinite Scroll Trigger */}
          {useInfiniteScroll && (
            <>
              {isFetchingNextPage && (
                <div className="mt-8 flex justify-center">
                  <CircularProgress size={32} />
                </div>
              )}
              <div ref={observerTarget} className="h-4" />
              {!hasNextPage && totalFilteredCount > 0 && (
                <div className="mt-8 text-center text-sm text-slate-500 dark:text-slate-400">
                  Showing all {totalFilteredCount} rules
                  {scopeFilter !== "all" && totalCount !== totalFilteredCount && (
                    <span className="ml-2">(filtered from {totalCount} total)</span>
                  )}
                </div>
              )}
            </>
          )}

          {/* Pagination (Specific Agent Type tabs only) */}
          {!useInfiniteScroll && data && totalFilteredCount > 0 && (
            <div className="mt-8 flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
              {/* Summary */}
              <div className="text-sm text-slate-600 dark:text-slate-400">
                Showing {totalFilteredCount > 0 ? (page - 1) * pageSize + 1 : 0}-
                {Math.min(page * pageSize, totalFilteredCount)} of {totalFilteredCount} rules
                {scopeFilter !== "all" && data.total !== totalFilteredCount && (
                  <span className="ml-2 text-slate-400">(filtered from {data.total} total)</span>
                )}
              </div>

              {/* Pagination Controls */}
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, newPage) => setPage(newPage)}
                color="primary"
                shape="rounded"
                showFirstButton
                showLastButton
              />

              {/* Page Size Selector */}
              <TextField
                select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                size="small"
                className="w-32"
              >
                <MenuItem value={10}>10 / page</MenuItem>
                <MenuItem value={20}>20 / page</MenuItem>
                <MenuItem value={50}>50 / page</MenuItem>
                <MenuItem value={100}>100 / page</MenuItem>
              </TextField>
            </div>
          )}
        </div>
      </main>

      {/* Sidebar for Create/Edit */}
      <RulesSidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        mode={sidebarMode}
        rule={selectedRule}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        documentCount={1}
        isDeleting={deleteRuleMutation.isPending}
        onConfirm={() => confirmDelete()}
        onCancel={cancelDelete}
      />
    </div>
  );
}
